
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCreateDetailedProduct } from '@/hooks/use-product-management';
import { toast } from '@/hooks/use-toast';
import { Plus } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useGetCategories } from '@/hooks/use-categories';

interface ProductFormProps {
  onProductAdded: () => void;
}

export function ProductForm({ onProductAdded }: ProductFormProps) {
  const { data: categories } = useGetCategories();
  const [newProduct, setNewProduct] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [unitOfMeasure, setUnitOfMeasure] = useState<string>('kg');
  const createProduct = useCreateDetailedProduct();

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProduct.trim()) return;
    
    try {
      await createProduct.mutateAsync({
        name: newProduct,
        category: selectedCategory || undefined,
        description: description || undefined,
        unit_of_measure: unitOfMeasure
      });
      
      toast({
        title: 'Product Added',
        description: `${newProduct} has been added successfully.`,
      });
      setNewProduct('');
      setDescription('');
      setSelectedCategory('');
      setUnitOfMeasure('kg');
      onProductAdded();
    } catch (error) {
      console.error('Failed to add product:', error);
      toast({
        title: 'Error',
        description: 'Failed to add product. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <form onSubmit={handleAddProduct} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div>
        <Label htmlFor="product-name">Product Name</Label>
        <Input
          id="product-name"
          value={newProduct}
          onChange={(e) => setNewProduct(e.target.value)}
          placeholder="Enter product name"
          className="w-full"
        />
      </div>
      
      <div>
        <Label htmlFor="category">Category (Optional)</Label>
        <Select
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          <SelectTrigger id="category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {categories?.map(category => (
              <SelectItem key={category} value={category}>{category}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="unit">Unit of Measure</Label>
        <Select
          value={unitOfMeasure}
          onValueChange={setUnitOfMeasure}
        >
          <SelectTrigger id="unit">
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="kg">Kilogram (kg)</SelectItem>
            <SelectItem value="g">Gram (g)</SelectItem>
            <SelectItem value="ton">Ton</SelectItem>
            <SelectItem value="l">Liter (l)</SelectItem>
            <SelectItem value="ml">Milliliter (ml)</SelectItem>
            <SelectItem value="pcs">Pieces (pcs)</SelectItem>
            <SelectItem value="box">Box</SelectItem>
            <SelectItem value="case">Case</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex items-end">
        <Button type="submit" disabled={createProduct.isPending} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>
      
      <div className="col-span-full">
        <Label htmlFor="description">Description (Optional)</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter product description"
          className="w-full h-20"
        />
      </div>
    </form>
  );
}
